import { useEffect, useState } from 'react'
import type { LayoutConfig, LayoutCatalog, LayoutRow, LayoutPreview } from '../types'
import { getLayout, saveLayout, resetLayout, getCatalog, previewLayout } from '../services/reportLayoutApi'
import { LayoutRowForm } from '../components/LayoutRowForm'

const KEY = 'lagedaten_table'
const move = <T,>(arr: T[], i: number, d: number): T[] => {
  const j = i + d
  if (j < 0 || j >= arr.length) return arr
  const copy = [...arr]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy
}
const emptyRow = (): LayoutRow => ({ label: '', source: '', row_type: 'single', visibility: 'both' })

export function ReportLayoutBuilder() {
  const [config, setConfig] = useState<LayoutConfig | null>(null)
  const [catalog, setCatalog] = useState<LayoutCatalog | null>(null)
  const [ags, setAgs] = useState('09162000')
  const [preview, setPreview] = useState<LayoutPreview | null>(null)
  const [status, setStatus] = useState('')

  useEffect(() => {
    getLayout(KEY).then((l) => setConfig(l.config))
    getCatalog().then(setCatalog)
  }, [])

  if (!config || !catalog) return <div className="p-6">Lädt…</div>

  const update = (fn: (c: LayoutConfig) => LayoutConfig) => setConfig(fn(structuredClone(config)))

  const renderRows = (rows: LayoutRow[], setRows: (mutate: (rows: LayoutRow[]) => void) => void) => (
    <>
      {rows.map((z, zi) => (
        <div key={zi} className="flex gap-2">
          <div className="flex-1">
            <LayoutRowForm row={z} catalog={catalog}
              onChange={(nr) => setRows((rr) => { rr[zi] = nr })}
              onRemove={() => setRows((rr) => { rr.splice(zi, 1) })} />
          </div>
          <div className="flex flex-col gap-1">
            <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => setRows((rr) => { if (zi > 0) { const t = rr[zi - 1]; rr[zi - 1] = rr[zi]; rr[zi] = t } })}>↑</button>
            <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => setRows((rr) => { if (zi < rows.length - 1) { const t = rr[zi + 1]; rr[zi + 1] = rr[zi]; rr[zi] = t } })}>↓</button>
          </div>
        </div>
      ))}
      <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => setRows((rr) => { rr.push(emptyRow()) })}>+ Zeile</button>
    </>
  )

  const onSave = async () => {
    try { await saveLayout(KEY, config); setStatus('Gespeichert.') }
    catch (e: any) { setStatus('Fehler: ' + (e?.response?.data?.detail ?? e.message)) }
  }
  const onReset = async () => { const l = await resetLayout(KEY); setConfig(l.config); setStatus('Zurückgesetzt.') }
  const onPreview = async () => setPreview(await previewLayout(KEY, ags))

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-2 items-center">
        <h1 className="text-2xl font-bold text-gut8-anthracite flex-1">Lagedaten-Tabelle (Layout)</h1>
        <input className="w-32 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={ags} onChange={(e) => setAgs(e.target.value)} />
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted" onClick={onPreview}>Vorschau</button>
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted" onClick={onReset}>Zurücksetzen</button>
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 bg-primary text-text-inverse font-semibold text-sm hover:bg-primary-hover disabled:opacity-40" onClick={onSave}>Speichern</button>
      </div>
      {status && <div className="text-sm text-text-muted">{status}</div>}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-4">
          {config.groups.map((g, gi) => (
            <div key={gi} className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4">
              <div className="flex gap-2 items-center mb-2">
                <span className="text-sm font-semibold text-gut8-anthracite">{g.ebene}</span>
                <input className="flex-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={g.titel_template}
                       placeholder="Titel-Template, z.B. Makrolage für {kreis}"
                       onChange={(e) => update((c) => { c.groups[gi].titel_template = e.target.value; return c })} />
                <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={g.rating_key ?? ''}
                        onChange={(e) => update((c) => { c.groups[gi].rating_key = e.target.value || null; return c })}>
                  <option value="">(kein Rating)</option>
                  {catalog.rating_keys.map((rk) => <option key={rk} value={rk}>{rk}</option>)}
                </select>
                <button className="text-sm text-secondary hover:opacity-80" onClick={() => update((c) => { c.groups.splice(gi, 1); return c })}>Gruppe löschen</button>
              </div>
              {g.bereiche.map((b, bi) => (
                <div key={bi} className="border-l-2 border-border-gut8-light pl-3 mb-3">
                  <div className="flex gap-2 items-center">
                    <input className="flex-1 px-3 py-2 text-sm font-medium border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={b.titel}
                           onChange={(e) => update((c) => { c.groups[gi].bereiche[bi].titel = e.target.value; return c })} />
                    <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={b.rating_key ?? ''}
                            onChange={(e) => update((c) => { c.groups[gi].bereiche[bi].rating_key = e.target.value || null; return c })}>
                      <option value="">(kein Rating)</option>
                      {catalog.rating_keys.map((rk) => <option key={rk} value={rk}>{rk}</option>)}
                    </select>
                    <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => update((c) => { c.groups[gi].bereiche = move(c.groups[gi].bereiche, bi, -1); return c })}>↑</button>
                    <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => update((c) => { c.groups[gi].bereiche = move(c.groups[gi].bereiche, bi, 1); return c })}>↓</button>
                    <button className="text-sm text-secondary hover:opacity-80" onClick={() => update((c) => { c.groups[gi].bereiche.splice(bi, 1); return c })}>✕</button>
                  </div>
                  {b.subgruppen.map((s, si) => {
                    const setRows = (mutate: (rows: LayoutRow[]) => void) => update((c) => {
                      const s2 = c.groups[gi].bereiche[bi].subgruppen[si]
                      if (!s2.zeilen) s2.zeilen = []
                      mutate(s2.zeilen); return c
                    })
                    return (
                      <div key={si} className="border-l-2 border-border-gut8-light pl-3 mt-2 space-y-2">
                        <div className="flex gap-2 items-center">
                          <input className="flex-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={s.label ?? ''} placeholder="Kategorie/Subgruppe (leer = ohne Titel)"
                                 onChange={(e) => update((c) => { c.groups[gi].bereiche[bi].subgruppen[si].label = e.target.value || null; return c })} />
                          <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={s.rating_key ?? ''}
                                  onChange={(e) => update((c) => { c.groups[gi].bereiche[bi].subgruppen[si].rating_key = e.target.value || null; return c })}>
                            <option value="">(kein Rating)</option>
                            {catalog.rating_keys.map((rk) => <option key={rk} value={rk}>{rk}</option>)}
                          </select>
                          <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => update((c) => { const sg = c.groups[gi].bereiche[bi].subgruppen; if (si>0) [sg[si-1],sg[si]]=[sg[si],sg[si-1]]; return c })}>↑</button>
                          <button className="p-1 rounded-gut8 border border-border-gut8-medium bg-white text-text-muted hover:bg-gut8-bg-muted" onClick={() => update((c) => { const sg = c.groups[gi].bereiche[bi].subgruppen; if (si<sg.length-1) [sg[si+1],sg[si]]=[sg[si],sg[si+1]]; return c })}>↓</button>
                          <button className="text-sm text-secondary hover:opacity-80" onClick={() => update((c) => { c.groups[gi].bereiche[bi].subgruppen.splice(si, 1); return c })}>✕</button>
                        </div>
                        {s.zeilen && renderRows(s.zeilen, setRows)}
                        {s.legs?.map((leg, li) => {
                          const setLegRows = (mutate: (rows: LayoutRow[]) => void) => update((c) => {
                            mutate(c.groups[gi].bereiche[bi].subgruppen[si].legs![li].zeilen); return c
                          })
                          return (
                            <div key={li} className="border-l-2 border-border-gut8-light pl-3 space-y-2">
                              <div className="text-xs italic text-text-muted">{leg.label ?? '(Leg)'}</div>
                              {renderRows(leg.zeilen ?? [], setLegRows)}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                  <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => update((c) => { c.groups[gi].bereiche[bi].subgruppen.push({ label: 'Neue Kategorie', rating_key: null, zeilen: [] }); return c })}>+ Kategorie</button>
                </div>
              ))}
              <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => update((c) => { c.groups[gi].bereiche.push({ titel: 'Neuer Bereich', rating_key: null, subgruppen: [] }); return c })}>+ Bereich</button>
            </div>
          ))}
          <div className="flex gap-4">
            <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => update((c) => { c.groups.push({ ebene: 'makro', titel_template: 'Makrolage für {kreis}', rating_key: null, bereiche: [] }); return c })}>+ Makro-Gruppe</button>
            <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => update((c) => { c.groups.push({ ebene: 'mikro', titel_template: 'Mikrolage für {gemeinde}', rating_key: null, bereiche: [] }); return c })}>+ Mikro-Gruppe</button>
          </div>
        </div>

        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 overflow-auto">
          <h2 className="text-sm font-semibold text-gut8-anthracite mb-2">Vorschau</h2>
          {preview ? (
            <>
              <h3 className="text-sm font-semibold text-gut8-anthracite mt-2">Prompt-Datenblock</h3>
              <pre className="bg-gut8-bg-muted rounded-gut8 p-3 text-xs whitespace-pre-wrap text-gut8-anthracite">{preview.markdown_prompt}</pre>
              <h3 className="text-sm font-semibold text-gut8-anthracite mt-2">Markdown (Web/.md)</h3>
              <pre className="bg-gut8-bg-muted rounded-gut8 p-3 text-xs whitespace-pre-wrap text-gut8-anthracite">{preview.markdown_web}</pre>
            </>
          ) : <div className="text-sm text-text-muted">„Vorschau" klicken.</div>}
        </div>
      </div>
    </div>
  )
}
